import { AlertTriangle, ExternalLink, ImageOff, RotateCcw } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';

import type { AssetResponse } from '../../api';
import { OLA_BASE_PATH } from '../grid/OlaPreview';

/**
 * A banner at full size in the detail overlay, plus what validation found.
 *
 * Two things distinguish this from the grid's hover preview. It plays without
 * a hover — the overlay is opened deliberately, so waiting for a pointer gesture
 * would just hide the content — and it carries a **Replay** button, because a
 * Hoxton unit loops three times and then freezes on its end frame. Everything
 * after those few seconds is a still image, so without a replay the only way to
 * watch a banner twice is to close the overlay and open it again.
 *
 * The report underneath is the diagnostic surface. When a banner renders wrong
 * the question is always "what is it missing?", and the answer is the list of
 * references the validator could not resolve — the one place that list exists.
 */

interface OlaStageProps {
  asset: AssetResponse;
  /** Capability token. Absent means the poster, with an explanation. */
  token: string | undefined;
  /** Signed URL for the fallback still, shown when the ad cannot be played. */
  posterUrl: string | undefined;
}

const FALLBACK_SIZE = { width: 300, height: 250 };

/** Room left around the banner so it never touches the stage edges. */
const STAGE_PADDING = 24;

export function OlaStage({ asset, token, posterUrl }: OlaStageProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  // Remounting the iframe is the only reliable replay: the animation is a GSAP
  // timeline inside a sandboxed, cross-origin-by-design frame, so there is no
  // handle to seek from out here. A fresh `key` throws the document away and
  // the banner starts from frame one.
  const [runId, setRunId] = useState(0);

  const width = asset.width ?? FALLBACK_SIZE.width;
  const height = asset.height ?? FALLBACK_SIZE.height;

  // Layout effect, not a plain one: measuring after paint would render the
  // banner at 1:1 for a frame and then snap it down, which reads as a flicker
  // on every open.
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => {
      const available = stage.getBoundingClientRect();
      setScale(
        Math.min(
          1,
          (available.width - STAGE_PADDING * 2) / width,
          (available.height - STAGE_PADDING * 2) / height,
        ),
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [width, height]);

  const report = asset.ola_report;
  const canPlay = token !== undefined && asset.ola_entry_path !== null;

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-3 p-4">
      <div
        ref={stageRef}
        className="relative flex min-h-0 w-full flex-1 items-center justify-center"
      >
        {canPlay ? (
          <div
            // Sized to the *scaled* box so the flex centring is honest: the
            // iframe itself keeps its natural pixel dimensions under the
            // transform, because a banner laid out at 240×200 is not the same
            // banner as one laid out at 300×250 and scaled — media queries and
            // Hoxton's own responsive.js both read the inner size.
            style={{ width: Math.round(width * scale), height: Math.round(height * scale) }}
            className="relative overflow-hidden rounded bg-white shadow-[0_12px_40px_-8px_rgb(15_23_42/0.35)]"
          >
            <iframe
              key={runId}
              src={`${OLA_BASE_PATH}/${token}/${asset.ola_entry_path}`}
              title={asset.filename_original}
              width={width}
              height={height}
              style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}
              // No `allow-same-origin`: the user's Okta token lives in
              // localStorage and this frame is same-origin with the app. See
              // the note in `OlaPreview.tsx`.
              sandbox="allow-scripts"
              referrerPolicy="no-referrer"
              scrolling="no"
              className="block border-0"
            />
          </div>
        ) : posterUrl ? (
          <img
            src={posterUrl}
            alt={asset.filename_original}
            className="relative max-h-full max-w-full rounded object-contain shadow-[0_12px_40px_-8px_rgb(15_23_42/0.35)]"
          />
        ) : (
          <div className="relative flex flex-col items-center gap-2 text-slate-400">
            <ImageOff className="h-8 w-8" strokeWidth={1.5} />
            <span className="text-[12.5px]">Preview unavailable</span>
          </div>
        )}
      </div>

      <div className="relative flex w-full max-w-2xl shrink-0 flex-col gap-2">
        <div className="flex items-center gap-2 text-[11.5px] text-slate-500">
          <button
            type="button"
            onClick={() => setRunId((id) => id + 1)}
            disabled={!canPlay}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white/90 px-2 py-1 font-medium text-slate-600 shadow-sm transition-colors hover:text-[#0014cc] disabled:opacity-40 disabled:hover:text-slate-600"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
            Replay
          </button>
          <span className="rounded bg-white/80 px-1.5 py-0.5">
            {width}×{height} HTML5
          </span>
          {report !== null && report.external_refs.length > 0 && (
            <span
              className="inline-flex items-center gap-1 rounded bg-white/80 px-1.5 py-0.5"
              // The full list rather than a count: "which third party does this
              // ad call?" is the question, and it is normally one or two hosts.
              title={report.external_refs.join('\n')}
            >
              <ExternalLink className="h-3 w-3" strokeWidth={2} />
              {report.external_refs.length} external
            </span>
          )}
          {!canPlay && (
            <span className="text-slate-400">
              {asset.ola_entry_path === null
                ? 'No entry file recorded for this OLA'
                : 'Preview unavailable — showing the fallback still'}
            </span>
          )}
        </div>

        {/* Warnings are shown, not hidden behind a toggle. A banner missing a
            reference usually still renders — just wrong — so the list is the
            only signal that anything is off. */}
        {report && report.warnings.length > 0 && (
          <ul className="max-h-24 overflow-y-auto rounded-md border border-amber-200 bg-amber-50/90 px-2.5 py-1.5 text-[11px] text-amber-900">
            {report.warnings.map((issue, index) => (
              <li key={`${issue.code}-${index}`} className="flex items-start gap-1.5 py-0.5">
                <AlertTriangle className="mt-px h-3 w-3 shrink-0" strokeWidth={2} />
                {/* The backend's message already names the reference and the
                    file that asked for it, so repeating either here would just
                    say it twice. `code` is the fallback for a finding from a
                    future validator that doesn't write prose. */}
                <span>{issue.message ?? issue.code}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
